import { FC, useCallback, useEffect, useState } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  Accordion,
  AccordionItem,
  Button,
  DataTableSkeleton,
  Link as CarbonLink,
  Tag,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { format0 } from 'utils/number';
import { runAuditLoadableAtom } from './atoms';
import type {
  AuditStatus,
  CheckName,
  CheckResultsResponse,
  CheckState,
  CheckStatus,
  DriftRecord,
  RunAuditResponse,
} from './types';
import styles from './page.module.scss';

// ─── Column helpers ───────────────────────────────────────────────────────────

const col = createColumnHelper<DriftRecord>();

// String-keyed accessor for DriftRecord (excludes the numeric/symbol keys
// that `keyof` would otherwise include via the Record<string, unknown> base).
type DriftRecordKey = Extract<keyof DriftRecord, string>;

// Renders an ID cell linked to the corresponding Salesforce record viewer
// when a paired *Href accessor is populated. Falls back to plain text or
// '--' when the id or href is missing.
const idLinkColumn = (
  field: DriftRecordKey,
  hrefField: DriftRecordKey,
  header: string,
  size = 170
) =>
  col.accessor(field, {
    header,
    size,
    cell: (info) => {
      const id = info.getValue();
      const href = info.row.original[hrefField];
      if (!id || !href) return (id as string | null | undefined) ?? '--';
      return (
        <CarbonLink
          href={String(href)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {String(id)}
        </CarbonLink>
      );
    },
  });

const subtractIdColumn = idLinkColumn(
  'subtractId',
  'subtractHref',
  'Subtract ID'
);

const subtractNameColumn = col.accessor('subtractName', {
  header: 'Subtract Name',
  size: 130,
  cell: (info) => info.getValue() ?? '--',
});

const tractIdColumn = idLinkColumn('tractId', 'tractHref', 'Tract ID');

const tractNameColumn = col.accessor('tractName', {
  header: 'Tract Name',
  size: 110,
  cell: (info) => info.getValue() ?? '--',
});

const bosUpdatedAtColumn = col.accessor('bosUpdatedAt', {
  header: 'BoS updated',
  size: 110,
  // Render date-only (the raw backend value is a full Postgres timestamp,
  // which would otherwise overflow the column width).
  cell: (info) => {
    const v = info.getValue();
    if (!v) return '--';
    return String(v).slice(0, 10);
  },
});

// ─── Columns per check ────────────────────────────────────────────────────────

const subtractUnitsDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  subtractIdColumn,
  subtractNameColumn,
  tractIdColumn,
  tractNameColumn,
  col.accessor('bosUnits', { header: 'BoS units', size: 90 }),
  col.accessor('tractUnits', { header: 'Tract units', size: 100 }),
  col.accessor('storedNri', { header: 'Stored NRI', size: 110 }),
  bosUpdatedAtColumn,
];

const tractUnitsDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  tractIdColumn,
  tractNameColumn,
  col.accessor('bosUnits', { header: 'BoS units', size: 90 }),
  col.accessor('tractUnits', { header: 'Tract units', size: 100 }),
  col.accessor('storedNri', { header: 'Stored NRI', size: 110 }),
  bosUpdatedAtColumn,
];

const subtractAcreageDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  subtractIdColumn,
  subtractNameColumn,
  tractIdColumn,
  tractNameColumn,
  col.accessor('bosGrossAcres', { header: 'BoS gross_acres', size: 130 }),
  col.accessor('sourceGrossAcres', { header: 'Source gross_acres', size: 150 }),
  bosUpdatedAtColumn,
];

const tractAcreageDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  tractIdColumn,
  tractNameColumn,
  col.accessor('bosGrossAcres', { header: 'BoS gross_acres', size: 130 }),
  col.accessor('sourceGrossAcres', { header: 'Source gross_acres', size: 150 }),
  bosUpdatedAtColumn,
];

const subtractLeaseRoyaltyRateDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  subtractIdColumn,
  subtractNameColumn,
  tractIdColumn,
  tractNameColumn,
  col.accessor('bosLeaseRoyaltyRate', { header: 'BoS rate', size: 100 }),
  col.accessor('sourceLeaseRoyaltyRate', { header: 'SF rate', size: 100 }),
  bosUpdatedAtColumn,
];

const subtractNriRecomputeDriftColumns = [
  col.accessor('id', { header: 'BoS ID', size: 70 }),
  subtractIdColumn,
  subtractNameColumn,
  tractIdColumn,
  tractNameColumn,
  col.accessor('storedNri', { header: 'Stored NRI', size: 110 }),
  col.accessor('expectedNri', { header: 'Expected NRI', size: 120 }),
  col.accessor('delta', { header: 'Delta', size: 110 }),
];

const COLUMNS_BY_CHECK: Record<CheckName, ColumnDef<DriftRecord>[]> = {
  subtract_units_drift: subtractUnitsDriftColumns as ColumnDef<DriftRecord>[],
  tract_units_drift: tractUnitsDriftColumns as ColumnDef<DriftRecord>[],
  subtract_acreage_drift:
    subtractAcreageDriftColumns as ColumnDef<DriftRecord>[],
  tract_acreage_drift: tractAcreageDriftColumns as ColumnDef<DriftRecord>[],
  subtract_lease_royalty_rate_drift:
    subtractLeaseRoyaltyRateDriftColumns as ColumnDef<DriftRecord>[],
  subtract_nri_recompute_drift:
    subtractNriRecomputeDriftColumns as ColumnDef<DriftRecord>[],
};

// ─── Check metadata ───────────────────────────────────────────────────────────

const CHECK_META: Array<{
  name: CheckName;
  label: string;
  description: string;
}> = [
  {
    name: 'subtract_units_drift',
    label: 'Subtract — net_acreage_units drift',
    description:
      "Subtract BoS rows where bos.net_acreage_units disagrees with the parent tract's netAcreageUnits__c. This is the smoking-gun signal for the INS-9423 contamination pattern. Until the INS-9423 backfill ships, expect ~56 rows here — that's the known set and confirms the audit is wired correctly.",
  },
  {
    name: 'tract_units_drift',
    label: 'Tract — net_acreage_units drift',
    description:
      'Tract-rooted BoS rows where bos.net_acreage_units disagrees with the tract row itself. Expected steady-state 0 — INS-9423 did not surface any tract-level drift, but the symmetry is worth monitoring.',
  },
  {
    name: 'subtract_acreage_drift',
    label: 'Subtract — gross_acres drift',
    description:
      "Subtract BoS rows where bos.gross_acres differs from the subtract's current Subtract_Gross_Acres__c by more than 1e-8. Catches acreage edits that did not propagate to BoS.",
  },
  {
    name: 'tract_acreage_drift',
    label: 'Tract — gross_acres drift',
    description:
      "Tract-rooted BoS rows where bos.gross_acres differs from the tract's Gross_Acres__c by more than 1e-8.",
  },
  {
    name: 'subtract_lease_royalty_rate_drift',
    label: 'Subtract — lease_royalty_rate drift',
    description:
      "Subtract BoS rows where bos.lease_royalty_rate differs from the subtract's Instrument_Lease_Royalty__c (when both are set and non-zero). Catches royalty-rate edits that did not propagate to BoS.",
  },
  {
    name: 'subtract_nri_recompute_drift',
    label: 'Subtract — NRI recompute drift',
    description:
      'Subtract BoS rows whose stored bos.nri does not match a fresh recompute via subtract.calculated_nri (within 1e-8 tolerance). The recompute reads instrument_type / netAcreageUnits / gross_acres / net_acres live from the subtract+tract rows, so this catches the cascade-race class of contamination (INS-9423) regardless of whether the units field itself agrees. Caveat: the recompute reads lease_royalty_rate / royalty_burdens / orri_burdens / lease_royalty_type from the stored BoS (via cached_bos_source in SubtractInterestCalculation), so drift in those fields is NOT caught here — partial coverage only via the lease_royalty_rate check above. Capped at 1000 results; check Sidekiq logs if you suspect the cap was hit. Skips rows where the calculator returns nil (UMI, OGL+NRA, missing acreage).',
  },
];

const buildInitialChecks = (): Record<CheckName, CheckState> =>
  Object.fromEntries(
    CHECK_META.map(({ name }) => [
      name,
      { status: 'pending' as const, count: 0, records: [] as DriftRecord[] },
    ])
  ) as Record<CheckName, CheckState>;

// ─── Check accordion item ─────────────────────────────────────────────────────

const CheckAccordionItem: FC<{
  name: CheckName;
  label: string;
  description: string;
  state: CheckState;
}> = ({ name, label, description, state }) => {
  const titleSuffix =
    state.status === 'running'
      ? ' (running…)'
      : state.status === 'complete'
        ? ` (${format0(state.count)})`
        : state.status === 'error'
          ? ' (error)'
          : '';

  const numColumns = COLUMNS_BY_CHECK[name].length;

  return (
    <AccordionItem title={`${label}${titleSuffix}`} open={false}>
      <div className={styles.checkDescription}>{description}</div>
      {state.status === 'pending' && (
        <div className={styles.pendingState}>Waiting for audit to start…</div>
      )}
      {state.status === 'running' && (
        <DataTableSkeleton
          columnCount={numColumns}
          rowCount={5}
          size="xs"
          showHeader={false}
          showToolbar={false}
        />
      )}
      {state.status === 'error' && (
        <div className={styles.errorState}>{state.error ?? 'Check failed'}</div>
      )}
      {state.status === 'complete' && (
        <DataTable
          data={state.records}
          columns={COLUMNS_BY_CHECK[name]}
          getRowId={(row) => String(row.id)}
          persistenceKey={`bos-drift-audit-${name}`}
          config={{
            sorting: true,
            globalSearch: true,
            columnFilters: false,
            columnReordering: true,
            columnPinning: false,
            columnVisibility: true,
            export: true,
          }}
        />
      )}
    </AccordionItem>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const BosDriftAuditPage: FC = () => {
  const [runState, triggerRun] = useAtom(runAuditLoadableAtom);

  const [auditStatus, setAuditStatus] = useState<AuditStatus>('idle');
  const [auditId, setAuditId] = useState<string | null>(null);
  const [auditChannel, setAuditChannel] = useState<string | null>(null);
  const [grandTotal, setGrandTotal] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const [checks, setChecks] =
    useState<Record<CheckName, CheckState>>(buildInitialChecks);

  const setCheckStatus = useCallback(
    (name: CheckName, status: CheckStatus, extra?: Partial<CheckState>) => {
      setChecks((prev) => ({
        ...prev,
        [name]: { ...prev[name], status, ...extra },
      }));
    },
    []
  );

  const fetchCheckResults = useCallback(
    async (currentAuditId: string, checkName: CheckName) => {
      setCheckStatus(checkName, 'running');
      const response = await request.get<CheckResultsResponse>(
        `/admin/bos_drift_audit/results/${currentAuditId}?check=${checkName}`
      );
      if ('error' in response) {
        setCheckStatus(checkName, 'error', {
          error: (response as { error: string }).error,
        });
      } else {
        setCheckStatus(checkName, 'complete', {
          count: response.count,
          records: response.records,
        });
      }
    },
    [setCheckStatus]
  );

  useEffect(() => {
    if (!auditId || !auditChannel) return;

    const subscription = subscribe(auditChannel);

    subscription.bind(
      'check_complete',
      (data: { check: CheckName; count: number }) => {
        fetchCheckResults(auditId, data.check);
      }
    );

    subscription.bind(
      'check_error',
      (data: { check: CheckName; error: string }) => {
        setCheckStatus(data.check, 'error', { error: data.error });
      }
    );

    subscription.bind(
      'audit_complete',
      (data: { grand_total: number; elapsed_seconds: number }) => {
        setGrandTotal(data.grand_total);
        setElapsedSeconds(data.elapsed_seconds);
        setAuditStatus('complete');
        setChecks((prev) => {
          CHECK_META.forEach(({ name }) => {
            if (prev[name].status === 'pending')
              fetchCheckResults(auditId, name);
          });
          return prev;
        });
      }
    );

    subscription.bind('audit_error', () => setAuditStatus('error'));

    return () => {
      subscription.unbind_all?.();
    };
  }, [auditId, auditChannel, fetchCheckResults, setCheckStatus]);

  useEffect(() => {
    if (runState.state !== 'hasData' || !runState.data) return;
    const data = runState.data as RunAuditResponse;
    setAuditId(data.auditId);
    setAuditChannel(data.channel);
    setAuditStatus('running');
    setGrandTotal(null);
    setElapsedSeconds(null);
    setChecks(buildInitialChecks());
  }, [runState]);

  const isRunning = auditStatus === 'running';
  const liveTotal = CHECK_META.reduce(
    (sum, { name }) => sum + checks[name].count,
    0
  );

  return (
    <>
      <PageHeader breadcrumbs={[]} title="BoS Drift Audit" actions={null} />

      <div className={styles.description}>
        <p>
          Scans <code>bundle_of_sticks_ownerships</code> rows for divergence
          between the persisted BoS and the parent Salesforce::Subtract /
          Salesforce::Tract values they were calculated from. Surfaces the exact
          class of contamination from{' '}
          <a
            href="https://srpatx.atlassian.net/browse/INS-9423"
            target="_blank"
            rel="noopener noreferrer"
          >
            INS-9423
          </a>
          .
        </p>
        <p className={styles.descriptionParagraph}>
          Steady state for every check is <strong>0</strong>. Non-zero counts
          indicate cascade bypass (raw-SQL tract updates, manual data fixes, new
          ingestion paths that skip callbacks) or a known contamination backlog
          that hasn&apos;t been backfilled yet. Until the INS-9423 backfill
          ships, the <em>Subtract — net_acreage_units drift</em> check should
          return ~56 known rows; that&apos;s the canary confirming the audit is
          finding real things.
        </p>
      </div>

      <div className={styles.toolbar}>
        <Button
          kind="primary"
          disabled={isRunning}
          onClick={() => triggerRun()}
        >
          {isRunning ? 'Running audit…' : 'Run audit'}
        </Button>

        {auditStatus === 'complete' && grandTotal !== null && (
          <span className={styles.status}>
            Completed in {elapsedSeconds}s — {format0(grandTotal)} drift rows
            found.
          </span>
        )}
        {auditStatus === 'error' && (
          <span className={styles.error}>Audit failed — see Sidekiq.</span>
        )}
        {runState.state === 'hasError' && (
          <span className={styles.error}>
            {(runState.error as Error)?.message ?? 'Failed to start audit'}
          </span>
        )}
      </div>

      <div className={styles.statsRow}>
        <span className={styles.statItem}>
          <span className={styles.statLabel}>Audit status:</span> {auditStatus}
        </span>
        <span className={styles.statItem}>
          <span className={styles.statLabel}>Checks complete:</span>{' '}
          {
            CHECK_META.filter((c) => checks[c.name].status === 'complete')
              .length
          }{' '}
          / {CHECK_META.length}
        </span>
        <span className={styles.statItem}>
          <span className={styles.statLabel}>Total drift rows:</span>{' '}
          {format0(liveTotal)}
          {auditStatus === 'complete' &&
            (liveTotal > 0 ? (
              <Tag type="red" size="sm">
                drift
              </Tag>
            ) : (
              <Tag type="green" size="sm">
                clean
              </Tag>
            ))}
        </span>
      </div>

      <Accordion>
        {CHECK_META.map(({ name, label, description }) => (
          <CheckAccordionItem
            key={name}
            name={name}
            label={label}
            description={description}
            state={checks[name]}
          />
        ))}
      </Accordion>
    </>
  );
};
